// «Уведомления на телефоне» в настройках (этап 5): push от самой платформы — заявки агента с кнопками
// «Одобрить / Отклонить» и тревоги, даже когда вкладка закрыта. Подписка — на каждом устройстве отдельно.
import { Button, Card, Chip, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Laptop, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';

interface PushView {
  publicKey: string | null;
  devices: Array<{
    id: number;
    device: string | null;
    endpoint: string;
    createdAt: string;
    lastOkAt: string | null;
    failures: number;
  }>;
}

const supported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** Понятное имя устройства для списка. */
function deviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Компьютер (Windows)';
  if (/Mac OS X/.test(ua)) return 'Компьютер (Mac)';
  return 'Устройство';
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ?? null;
}

export function PushCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['push'], queryFn: () => api<PushView>('/api/push') });
  const [mine, setMine] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [hasSw, setHasSw] = useState<boolean | null>(null);

  useEffect(() => {
    void (async () => {
      const reg = await registration();
      setHasSw(!!reg);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setMine(sub?.endpoint ?? null);
    })();
  }, []);

  const enable = async () => {
    const key = q.data?.publicKey;
    if (!key) return toast('Платформа ещё не готова к уведомлениям — попробуйте позже');
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast('Браузер не разрешил уведомления — разрешите их в настройках сайта');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      });
      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await api('/api/push/subscribe', { ...json, device: deviceName() });
      setMine(sub.endpoint);
      toast('Уведомления включены на этом устройстве');
      void qc.invalidateQueries({ queryKey: ['push'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number, endpoint: string) => {
    try {
      if (endpoint === mine) {
        const reg = await registration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        await sub?.unsubscribe();
        setMine(null);
      }
      await api(`/api/push/${id}`, undefined, 'DELETE');
      toast('Устройство отключено');
      void qc.invalidateQueries({ queryKey: ['push'] });
    } catch (e) {
      toast((e as Error).message);
    }
  };

  const test = async () => {
    try {
      const r = await api<{ sent: number; total: number }>('/api/push/test', {});
      toast(
        r.sent === r.total
          ? 'Проверочное уведомление отправлено'
          : `Отправлено на ${r.sent} из ${r.total} устройств`,
      );
    } catch (e) {
      toast((e as Error).message);
    }
  };

  const devices = q.data?.devices ?? [];
  const here = mine ? devices.find((d) => d.endpoint === mine) : undefined;
  return (
    <Card
      title="Уведомления на телефоне"
      action={
        devices.length ? (
          <Button size="sm" variant="ghost" icon={<BellRing />} onClick={() => void test()}>
            Проверить
          </Button>
        ) : undefined
      }
    >
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>
        Заявки агента приходят с кнопками «Одобрить» и «Отклонить» прямо в уведомлении, тревоги — сразу.
        Работает и при закрытой вкладке. Это в дополнение к Telegram.
      </p>
      {!supported() ? (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          Этот браузер не умеет push-уведомления.
        </p>
      ) : isIos() && !standalone() ? (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          На iPhone уведомления работают в установленной платформе: «Поделиться» → «На экран Домой», затем
          откройте AutoAFF с экрана и включите здесь.
        </p>
      ) : hasSw === false ? (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          Уведомления заработают в рабочей версии платформы (в режиме разработки их нет).
        </p>
      ) : here ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Chip tone="success" dot>
            Включены на этом устройстве
          </Chip>
        </div>
      ) : (
        <Button
          variant="primary"
          icon={<BellRing />}
          onClick={() => void enable()}
          disabled={busy || mine === undefined || !q.data}
        >
          {busy ? 'Включаю…' : 'Включить на этом устройстве'}
        </Button>
      )}
      {devices.length > 0 && (
        <ul className="push-list">
          {devices.map((d) => (
            <li key={d.id}>
              {/iPhone|iPad|Android/.test(d.device ?? '') ? <Smartphone /> : <Laptop />}
              <div className="min-w-0">
                <b>
                  {d.device ?? 'Устройство'}
                  {d.endpoint === mine ? ' · это устройство' : ''}
                </b>
                <span>
                  подключено {when(d.createdAt)}
                  {d.lastOkAt ? ` · последнее уведомление ${when(d.lastOkAt)}` : ''}
                </span>
              </div>
              <button
                type="button"
                className="auto-x"
                aria-label={`Отключить ${d.device ?? 'устройство'}`}
                onClick={() => void remove(d.id, d.endpoint)}
              >
                <X />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
